import { Box } from '@mui/material'
import dayjs from 'dayjs'
import { L } from '../ui/LavDropdown'

/** Centered "TODAY" / "WEDNESDAY · JUN 10" divider between message days. */
export function DaySeparator({ label }) {
	const line = { flex: 1, height: '1px', bgcolor: L.border }
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', my: '6px' }}>
			<Box sx={line} />
			<Box component="span" sx={{
				fontSize: '11px', fontWeight: 750, color: L.muted,
				letterSpacing: '0.06em', textTransform: 'uppercase', flex: 'none',
			}}>
				{label}
			</Box>
			<Box sx={line} />
		</Box>
	)
}

/**
 * Chat bubble — direction-aware:
 *   outbound → right-aligned, primary purple
 *   inbound  → left-aligned, white with border
 * Shows time (and Twilio delivery status for outbound) underneath.
 */
// Twilio's create-message call returns 'queued'/'accepted' — the enqueue state,
// not a delivery outcome. We don't yet receive StatusCallback webhooks, so those
// rows never progress and showing them literally read as permanently "Queued".
// Until the callback lands, treat enqueued as "Sent" and pass real outcomes
// ('delivered', 'failed', 'undelivered', …) through untouched.
const ENQUEUED_STATUSES = new Set(['queued', 'accepted', 'sending', 'scheduled'])

const statusLabel = (raw) => {
	if (!raw) return 'Sent'
	const s = String(raw).toLowerCase()
	if (ENQUEUED_STATUSES.has(s)) return 'Sent'
	return s.charAt(0).toUpperCase() + s.slice(1)
}

export function MessageBubble({ msg, maxWidth = '62%' }) {
	const isOutbound = (msg.direction || 'outbound') === 'outbound'
	const status = isOutbound ? statusLabel(msg.twilio_status) : null
	return (
		<Box sx={{
			display: 'flex', flexDirection: 'column', maxWidth,
			alignSelf: isOutbound ? 'flex-end' : 'flex-start',
			alignItems: isOutbound ? 'flex-end' : 'flex-start',
		}}>
			<Box sx={{
				borderRadius: '16px', p: '9px 13px', fontSize: '13.5px', lineHeight: 1.45,
				wordBreak: 'break-word', whiteSpace: 'pre-wrap',
				...(isOutbound
					? { bgcolor: L.primary, color: L.onPrimary, borderBottomRightRadius: '6px' }
					: { bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, color: L.text, borderBottomLeftRadius: '6px' }),
			}}>
				{msg.message_text}
			</Box>
			<Box component="span" sx={{ fontSize: '11px', color: L.muted, fontWeight: 600, mt: '3px', px: '4px' }}>
				{dayjs(msg.created_at).format('h:mm A')}{status ? ` · ${status}` : ''}
			</Box>
		</Box>
	)
}
